import { computed, IReactionDisposer, makeObservable, reaction } from 'mobx';
import { stringify } from 'qs';

import { ENDPOINTS } from 'config/endpoints';
import { FormReviewFlatModeParams } from 'config/routes';
import { getApiUrl } from 'config/urls';
import { TableViewMode } from 'entities/Table';
import { FormActionsStore } from 'stores/common/form';
import {
  TableFactoryStore,
  TableControllerCommentsStore,
} from 'stores/common/table';
import { ILocalStore } from 'stores/interfaces';

import { ColumnsSettingsDrawerStore } from './ColumnsSettingsDrawerStore';

type FormReviewStoreParams = Omit<Partial<FormReviewFlatModeParams>, 'id'> & {
  id: number;
};

class FormReviewFlatTableStore implements ILocalStore {
  constructor(params: FormReviewStoreParams) {
    this._callParams = params;
    this.formActionsStore = new FormActionsStore({
      store: this.tableStore,
    });

    this._disposers.push(
      reaction(
        () => this.commentsStore.drawerState.opened,
        (state) => {
          if (!state) {
            this.tableStore.pubSub.publishCommentUnselectEvent();
          }
        }
      )
    );

    makeObservable(this, {
      exportExcelLink: computed,
    });
  }

  readonly tableStore: TableFactoryStore = new TableFactoryStore({
    showNotVisibleByDefaultColumns: false,
    virtualized: true,
  });

  readonly formActionsStore: FormActionsStore;

  private readonly _callParams: FormReviewStoreParams;

  readonly commentsStore = new TableControllerCommentsStore();

  readonly columnsSettingsDrawer = new ColumnsSettingsDrawerStore({
    headerStore: this.tableStore.tableHeaderStore,
  });

  private readonly _disposers: IReactionDisposer[] = [];

  get exportExcelLink(): { href?: string } {
    if (!this.tableStore.callParams) {
      return {};
    }

    const { form_id, view, root_record_id, visibility_level, sort } =
      this.tableStore.callParams;

    return {
      href: `${getApiUrl()}${ENDPOINTS.form.exportData}?${stringify({
        form_id,
        view,
        root_record_id,
        visibility_level,
        sort,
      })}`,
    };
  }

  async init(): Promise<void> {
    const unsubscribe = this.tableStore.pubSub.subscribeToCommentSelectEvent(
      this.commentsStore.handleCommentSelect
    );
    this._disposers.push(unsubscribe);
    await this.tableStore.init({
      root_record_id: this._callParams.rootId,
      form_id: this._callParams.id,
      view: TableViewMode.flat,
      limit: 10000, // Загружаем все данные для виртуализации
    });
    this.commentsStore.setParams({
      columns: this.tableStore.tableHeaderStore.columnModels,
    });
  }

  destroy() {
    this.tableStore.destroy();
    this.commentsStore.destroy();
    this._disposers.forEach((disposer) => disposer());
  }
}

export default FormReviewFlatTableStore;
